/**
 * CampaignActionBuilder - Construtor visual de ações pós-disparo (Etapa 6)
 *
 * v4.0.0 (Studio Layout): Tela única no padrão visual do Studio da Etapa 4
 * (MessageGroupManager). Não usa mais o `AddCampaignActionDialog` — toda
 * edição é inline no próprio card (accordion controlado por `activeId`).
 *
 * - Header: título + descrição + DropdownMenu "Adicionar Ação" (5 tipos)
 * - Cards: padrão cockpit branco com badge #01, dot pulsante, ring indigo,
 *   pill de ações ↑↓⧉🗑, corpo expandido bg-zinc-50/50
 * - Contrato de dados (CampaignActionItem) inalterado — backend zero touch.
 *
 * @author Sistema de Campanhas v4.0
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Zap,
  Lightbulb,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CampaignActionItem,
  CampaignActionType,
  ExecutionCondition,
  campaignActionConfig,
} from './campaign-actions-types';
import { CampaignActionCard, ActionValueOption } from './CampaignActionCard';

// ============================================================================
// PROPS
// ============================================================================

interface CampaignActionBuilderProps {
  items: CampaignActionItem[];
  onItemsChange: (items: CampaignActionItem[]) => void;
}

// Ordem dos tipos no menu "Adicionar Ação"
const ACTION_TYPES_ORDER: CampaignActionType[] = [
  'add_tag',
  'assign_phase',
  'assign_department',
  'assign_agent',
  'assign_global_queue',
  'start_flow',
];


// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function CampaignActionBuilder({
  items,
  onItemsChange,
}: CampaignActionBuilderProps) {
  const { currentTenant } = useAuth();

  // Card atualmente expandido (accordion). Por padrão: o último adicionado.
  const [activeId, setActiveId] = useState<string | null>(
    items.length > 0 ? items[0].id : null
  );

  // Garante que o activeId continue válido após remoções
  useEffect(() => {
    if (items.length > 0 && !items.find((i) => i.id === activeId)) {
      setActiveId(items[0].id);
    } else if (items.length === 0 && activeId !== null) {
      setActiveId(null);
    }
  }, [items, activeId]);

  // ---------------------------------------------------------------------------
  // Carregamento das listas de opções (tags, fases, departamentos, agentes, filas)
  // Mantém as MESMAS queries da versão anterior — sem alteração de RPCs/tabelas.
  // ---------------------------------------------------------------------------

  // Tags: agora trazem `color` para renderizar bolinha no select e header
  const { data: tags = [] } = useQuery({
    queryKey: ['action-builder-tags', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data } = await supabase
        .from('contact_tags')
        .select('id, name, color')
        .eq('tenant_id', currentTenant.id);
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  // Categorias do kanban (equivalente ao "pipeline" no kanban de conversas)
  // — usadas para agrupar as fases no popover de seleção
  const { data: kanbanCategories = [] } = useQuery({
    queryKey: ['action-builder-kanban-categories', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data } = await supabase
        .from('kanban_categories')
        .select('id, name')
        .eq('tenant_id', currentTenant.id);
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  // Fases: trazem color + category_id para agrupamento e bolinha
  const { data: phases = [] } = useQuery({
    queryKey: ['action-builder-phases', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data } = await supabase
        .from('kanban_phases')
        .select('id, name, color, category_id, position')
        .eq('tenant_id', currentTenant.id)
        .order('position', { ascending: true });
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['action-builder-departments', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', currentTenant.id);
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  // Membros do tenant via hook centralizado (cache compartilhado)
  const { data: tenantMembers = [] } = useTenantMembers({ onlyActive: false });
  const agents: ActionValueOption[] = useMemo(
    () =>
      tenantMembers.map((m) => ({
        id: m.user_id,
        name: m.full_name || m.email || 'Sem nome',
      })),
    [tenantMembers]
  );

  const { data: queues = [] } = useQuery({
    queryKey: ['action-builder-queues', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data } = await supabase
        .from('distribution_queues')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .in('context_type', ['chat', 'all']);
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });

  // Fluxos elegíveis para serem chamados por campanhas.
  // Filtrados via RPC list_campaign_eligible_flows: retorna apenas fluxos
  // ativos com gatilho 'campaign_action' configurado e que liberam esta
  // campanha (allow_all_campaigns OR allowed_campaign_ids contém o id).
  const { data: eligibleFlows = [] } = useQuery({
    queryKey: ['action-builder-eligible-flows', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('list_campaign_eligible_flows', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) {
        console.warn('[CampaignActionBuilder] Erro ao listar fluxos elegíveis:', error.message);
        return [];
      }
      return data || [];
    },
    enabled: !!currentTenant?.id,
  });



  // Mapa { id -> nome } para exibir nomes resolvidos no header dos cards
  const resolvedNames = useMemo(() => {
    const names: Record<string, string> = {};
    tags.forEach((t) => { names[t.id] = t.name; });
    phases.forEach((p) => { names[p.id] = p.name; });
    departments.forEach((d) => { names[d.id] = d.name; });
    agents.forEach((a) => { names[a.id] = a.name; });
    queues.forEach((q) => { names[q.id] = q.name; });
    eligibleFlows.forEach((f) => { names[f.flow_id] = f.name; });
    return names;
  }, [tags, phases, departments, agents, queues, eligibleFlows]);

  // Mapa { categoryId -> nome } para enriquecer fases com agrupamento
  const categoryNameById = useMemo(() => {
    const m: Record<string, string> = {};
    kanbanCategories.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [kanbanCategories]);

  // Retorna a lista de opções correta conforme o tipo da ação
  const getOptionsForType = (type: CampaignActionType): ActionValueOption[] => {
    switch (type) {
      case 'add_tag':
        return tags.map((t) => ({
          id: t.id,
          name: t.name,
          color: (t as { color?: string | null }).color ?? null,
        }));
      case 'assign_phase':
        return phases.map((p) => ({
          id: p.id,
          name: p.name,
          color: (p as { color?: string | null }).color ?? null,
          groupId: (p as { category_id?: string }).category_id,
          groupName:
            categoryNameById[(p as { category_id?: string }).category_id || ''] ||
            'Sem categoria',
        }));
      case 'assign_department':   return departments as ActionValueOption[];
      case 'assign_agent':        return agents;
      case 'assign_global_queue': return queues as ActionValueOption[];
      case 'start_flow':
        // Fluxos elegíveis (com gatilho 'campaign_action' ativo e whitelist OK)
        return eligibleFlows.map((f) => ({ id: f.flow_id, name: f.name }));
    }
  };


  // ---------------------------------------------------------------------------
  // Handlers — todos preservam o shape original de CampaignActionItem
  // ---------------------------------------------------------------------------

  /**
   * Adiciona uma nova ação do tipo escolhido no menu, já expandida para edição.
   */
  const handleAddAction = (type: CampaignActionType) => {
    const newItem: CampaignActionItem = {
      id: crypto.randomUUID(),
      action_type: type,
      action_value: {},
      execution_condition: 'all',
      sort_order: items.length,
    };
    onItemsChange([...items, newItem]);
    setActiveId(newItem.id);
  };

  /** Atualiza o valor selecionado (tag_id / phase_id / etc) da ação */
  const handleChangeValue = (item: CampaignActionItem, newValue: string) => {
    const valueKey = campaignActionConfig[item.action_type].valueKey;
    const newItems = items.map((it) =>
      it.id === item.id
        ? { ...it, action_value: { ...it.action_value, [valueKey]: newValue } }
        : it
    );
    onItemsChange(newItems);
  };

  /** Atualiza a condição de execução (all / success / error) */
  const handleChangeCondition = (item: CampaignActionItem, cond: ExecutionCondition) => {
    const newItems = items.map((it) =>
      it.id === item.id ? { ...it, execution_condition: cond } : it
    );
    onItemsChange(newItems);
  };

  /** Move uma ação para cima na lista (atualiza sort_order) */
  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    onItemsChange(newItems.map((it, idx) => ({ ...it, sort_order: idx })));
  };

  /** Move uma ação para baixo na lista (atualiza sort_order) */
  const handleMoveDown = (index: number) => {
    if (index >= items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    onItemsChange(newItems.map((it, idx) => ({ ...it, sort_order: idx })));
  };

  /** Duplica uma ação existente (novo id, mesmo conteúdo) */
  const handleDuplicate = (item: CampaignActionItem, index: number) => {
    const dup: CampaignActionItem = {
      ...item,
      id: crypto.randomUUID(),
      action_value: { ...item.action_value },
      sort_order: index + 1,
    };
    const newItems = [...items];
    newItems.splice(index + 1, 0, dup);
    onItemsChange(newItems.map((it, idx) => ({ ...it, sort_order: idx })));
    setActiveId(dup.id);
  };

  /** Remove uma ação e re-calcula sort_order dos demais */
  const handleRemove = (itemId: string) => {
    const newItems = items
      .filter((i) => i.id !== itemId)
      .map((it, idx) => ({ ...it, sort_order: idx }));
    onItemsChange(newItems);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* ====== Header da seção (espelha Etapa 4) ====== */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium">Ações Pós-Disparo</h3>
          <Badge variant="secondary" className="text-xs">
            {items.length} {items.length === 1 ? 'ação' : 'ações'}
          </Badge>
        </div>

        {/* DropdownMenu "Adicionar Ação" — substitui os 5 botões soltos */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              Adicionar Ação
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {ACTION_TYPES_ORDER.map((type) => {
              const cfg = campaignActionConfig[type];
              return (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleAddAction(type)}
                  className="gap-3 cursor-pointer"
                >
                  <div className={cn('p-1.5 rounded-md shrink-0', cfg.bgColor)}>
                    <cfg.icon className={cn('h-3.5 w-3.5', cfg.iconColor)} />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium">{cfg.label}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {cfg.description}
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ====== Subtítulo descritivo ====== */}
      <p className="text-sm text-muted-foreground -mt-2">
        Configure ações automáticas executadas após o disparo de cada contato.
      </p>

      {/* ====== Lista de cards Studio ou estado vazio ====== */}
      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, index) => {
            const cfg = campaignActionConfig[item.action_type];
            const valueId = item.action_value?.[cfg.valueKey];
            const resolvedName = valueId ? resolvedNames[valueId] : undefined;
            return (
              <CampaignActionCard
                key={item.id}
                item={item}
                index={index}
                total={items.length}
                isActive={item.id === activeId}
                resolvedName={resolvedName}
                options={getOptionsForType(item.action_type)}
                onToggleActive={() =>
                  setActiveId(activeId === item.id ? null : item.id)
                }
                onChangeValue={(v) => handleChangeValue(item, v)}
                onChangeCondition={(c) => handleChangeCondition(item, c)}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                onDuplicate={() => handleDuplicate(item, index)}
                onRemove={() => handleRemove(item.id)}
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-10 text-center">
          <div className="inline-flex p-3 rounded-full bg-indigo-50 mb-3">
            <Zap className="h-6 w-6 text-indigo-500" />
          </div>
          <p className="text-sm font-medium text-zinc-700">
            Nenhuma ação configurada
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            Clique em <span className="font-semibold">Adicionar Ação</span> acima
            para começar.
          </p>
        </div>
      )}

      {/* ====== Banner de dica (padrão "border-l-4" dos diálogos de gatilho do flows) ====== */}
      <div className="flex gap-3 rounded-lg border border-zinc-200 bg-white dark:bg-zinc-950 dark:border-zinc-800 border-l-4 border-l-amber-500 p-3.5">
        <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="text-sm leading-relaxed text-foreground">
          <p className="font-semibold mb-0.5">Dica</p>
          <p>
            Use condições para aplicar ações diferentes baseadas no resultado do envio.
            Por exemplo, adicione uma tag &quot;Falha Envio&quot; apenas para disparos com erro.
          </p>
        </div>
      </div>
    </div>
  );
}
